import { PageBackLink } from "@/components/page-back-link"
import { ListSearchFilters } from "@/components/list-search-filters"
import { PaymentFiltersSchema, PaymentStatusSchema } from "@optimist/schemas"
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import type { DataGridColumn } from "@/components/data-grid/data-grid"
import { StatusBadge } from '@/components/status-badge'
import { DataGrid } from '@/components/data-grid'
import { Pagination } from '@/components/pagination'
import { ErrorBanner, ErrorBoundary } from '@/components/error-boundary'
import { PageBreadcrumbs, PageHeader } from '@/components/page-header'
import { queryKeys } from '@/lib/query-keys'
import { api } from '@/lib/api'
import { formatDateTime, formatCurrency } from '@/lib/utils'
import { getRowIndexProps } from '@/lib/pagination'
import type { PortalPayment, PortalPaymentListResponse } from '@/features/payments/payment-detail'

/**
 * Payments Queue page
 * Purpose: Handle financial exceptions
 */
export const Route = createFileRoute('/dashboard/payments/')({
  component: PaymentsPage,
  validateSearch: (search) => PaymentFiltersSchema.parse(search),
})

function PaymentsPage() {
  const navigate = useNavigate({ from: Route.fullPath })
  const filters = Route.useSearch()
  const { page, limit } = filters

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.payments.list(filters),
    queryFn: () => api.get<PortalPaymentListResponse>('/payments', { params: filters }),
  })

  const onRowClicked = (row: PortalPayment) => {
    navigate({ to: '/dashboard/payments/$paymentId', params: { paymentId: row.id } })
  }

  const columnDefs: DataGridColumn<PortalPayment>[] = [
    {
      field: 'id',
      headerName: 'Payment ID',
      render: (row) => (
        <span className="font-mono text-meta">{row.id ? String(row.id).slice(0, 8) : ''}</span>
      ),
    },
    {
      field: 'orderId',
      headerName: 'Order',
      render: (row) => {
        const orderId = row.orderId
        return (
          <Link
  to="/dashboard/orders/$orderId"
  params={{ orderId }}
  className="text-primary hover:underline font-mono text-meta"
  onClick={(e) => e.stopPropagation()}
  >
            {orderId.slice(0, 8)}
          </Link>
        )
      },
    },
    {
      field: 'patientName',
      headerName: 'Patient',
      render: (row) => row.patientName || '-',
    },
    {
      field: 'amount',
      headerName: 'Amount',
      render: (row) => row.amount !== undefined ? formatCurrency(row.amount) : '-',
    },
    {
      field: 'refundedAmount',
      headerName: 'Refunded',
      render: (row) => row.refundedAmount !== undefined && row.refundedAmount > 0 ? formatCurrency(row.refundedAmount) : '-',
    },
    {
      field: 'refundCount',
      headerName: 'Refunds',
      render: (row) => typeof row.refundCount === 'number' && row.refundCount > 0 ? String(row.refundCount) : '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      render: (row) => {
        const statusVariants: Record<
          string,
          'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info'
        > = {
          pending: 'warning',
          authorized: 'success',
          failed: 'destructive',
          refunded: 'outline',
        }
        const configured = statusVariants[row.status]
        const rawValue = row.status
        const displayValue = rawValue ? rawValue.charAt(0).toUpperCase() + rawValue.slice(1) : ''
        const variant = configured ? configured : 'default'
        return (
          <StatusBadge variant={variant}>
            {displayValue}
          </StatusBadge>
        )
      },
    },
    {
      field: 'provider',
      headerName: 'Provider',
      render: (row) => (row.provider ? row.provider : '-'),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      render: (row) => row.createdAt ? formatDateTime(row.createdAt) : '-',
    },
  ]

  const response = data
  const payments = response ? response.data : []
  const meta = response ? response.meta : null
  const rowIndexProps = getRowIndexProps({ meta, page, limit, mode: 'descending' })
  const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
  const handleRetry = () => {
    void refetch()
  }

  return (
    <ErrorBoundary
      title="Payments page error"
      fallback={<ErrorBanner title="Payments unavailable" message="Something went wrong loading this page." />}
    >
      <div className="space-y-6">
        <PageHeader back={<PageBackLink to="/dashboard" label="Back to dashboard" />}
          breadcrumbs={
            <PageBreadcrumbs
              items={[
                <Link key="dashboard" to="/dashboard">Dashboard</Link>,
                <span key="payments">Payments</span>,
              ]}
            />
          }
          title="Payments"
        />

        <ListSearchFilters
          key={JSON.stringify([filters.search, filters.status])}
          title="Search and filter payments"
          placeholder="Payment ID, order ID, or patient name"
          search={filters.search}
          status={filters.status}
          statuses={PaymentStatusSchema.options.map((value) => ({ value, label: value.charAt(0) + value.slice(1).toLowerCase().replaceAll("_", " ") }))}
          onApply={(next) => navigate({ search: PaymentFiltersSchema.parse({ ...next, page: 1, limit }) })}
          onClear={() => navigate({ search: { page: 1, limit } })}
        />

        {error ? (
          <ErrorBanner title="Error loading payments" message={errorMessage} onRetry={handleRetry} />
        ) : null}

        <ErrorBoundary
          resetKey={payments.length}
          fallback={<ErrorBanner title="Payments table failed" message="Unable to render payment data." />}
        >
          <DataGrid<PortalPayment>
            rowData={payments}
            columnDefs={columnDefs}
            loading={isLoading}
            {...rowIndexProps}
            onRowClicked={onRowClicked}
          />
        </ErrorBoundary>

        {meta ? (
          <Pagination
            page={meta.page}
            limit={meta.limit}
            total={meta.total}
            totalPages={meta.totalPages}
            onPageChange={(nextPage) => {
              navigate({
                search: (prev) => ({
                  ...prev,
                  page: nextPage,
                  limit,
                }),
              })
            }}
            onLimitChange={(nextLimit) => {
              navigate({
                search: (prev) => ({
                  ...prev,
                  page: 1,
                  limit: nextLimit,
                }),
              })
            }}
          />
        ) : null}
      </div>
    </ErrorBoundary>
  )
}
